import HttpClient from "../utils/http-client";

export class ImpactReportService extends HttpClient {
  constructor(...args) {
    super(...args);
    // Array params (e.g. metrics) serialize to repeated params without
    // brackets — `metrics=a&metrics=b` — matching the outcomes proxy. `null`
    // is axios's sentinel for that mode (`false` keeps the brackets).
    // eslint-disable-next-line unicorn/no-null
    this.http.defaults.paramsSerializer = { indexes: null };
  }

  async getInputs(orgCode, reportType) {
    return await this.http.get(`/impact-reports/orgs/${orgCode}/inputs`, {
      params: { report_type: reportType },
    });
  }

  async saveInputs(orgCode, reportType, pack) {
    return await this.http.put(`/impact-reports/orgs/${orgCode}/inputs`, pack, {
      params: { report_type: reportType },
    });
  }

  /**
   * Generate a new deck from the given inputs pack. The backend saves the
   * pack first (same upsert as saveInputs) and then generates, so the deck
   * always matches the current form state. Expect ~15-60s — this request
   * overrides the client's 30s default timeout so slow generations aren't
   * killed client-side while the backend keeps working.
   */
  async generate(orgCode, reportType, pack) {
    return await this.http.post(
      `/impact-reports/orgs/${orgCode}/generate`,
      pack,
      { params: { report_type: reportType }, timeout: 65_000 },
    );
  }

  /** Generation history for the org, newest first — successes and failures. */
  async getDecks(orgCode) {
    return await this.http.get(`/impact-reports/orgs/${orgCode}/decks`);
  }

  /**
   * Dev tooling: every editable Gemini prompt dial for the report type,
   * resolved to the active stored version or the code default (isDefault).
   */
  async getPromptConfig(reportType) {
    return await this.http.get(`/impact-reports/prompt-config`, {
      params: { report_type: reportType },
    });
  }

  /** Dev tooling: store a new active version of one prompt field. */
  async savePrompt(reportType, field, body) {
    return await this.http.put(
      `/impact-reports/prompt-config/${field}`,
      { body },
      { params: { report_type: reportType } },
    );
  }

  /**
   * The data service's metric vocabulary, via the outcomes proxy. Not
   * tenant-scoped, and the only source for a metric's label.
   */
  async getMetricCatalog() {
    return await this.http.get(`/outcomes/v1/metrics/catalog`);
  }

  /**
   * Dashboard summary KPIs for one or more orgs and a period, via the
   * outcomes proxy. Always org-scoped: the proxy rejects out-of-scope
   * requests with a 403. The API takes a list of org codes — for a parent
   * org, the caller is responsible for including the child codes it wants
   * aggregated (there is no server-side hierarchy expansion). Empty params
   * are omitted rather than sent blank (the backend reads e.g. `metrics=`
   * as an empty-set filter). Param names mirror the proxied Data-Team
   * service (see src/lib/data-contracts/outcomes_openapi.json).
   *
   * @param {string|string[]} orgCodes - Org code(s) the values cover.
   * @param {object} [options]
   * @param {string} [options.startDate] - Inclusive range start (`YYYY-MM-DD`).
   * @param {string} [options.endDate] - Inclusive range end (`YYYY-MM-DD`).
   * @param {string[]} [options.metrics] - Metric keys to return; omitted
   *   returns the backend's default set.
   */
  async getDashboardSummary(orgCodes, { startDate, endDate, metrics } = {}) {
    return await this.http.get(`/outcomes/v1/dashboard/summary`, {
      params: {
        // serializes to repeated org_code params (see the constructor)
        org_code: Array.isArray(orgCodes) ? orgCodes : [orgCodes],
        ...(startDate ? { start_date: startDate } : {}),
        ...(endDate ? { end_date: endDate } : {}),
        ...(Array.isArray(metrics) && metrics.length > 0 ? { metrics } : {}),
      },
    });
  }
}
